import * as React from "react";
import Form from "./commons/components/form/Form";
import ModalPopup from "./commons/components/modal/ModalPopup";
import TreatmentImageInput from "./commons/components/form/input/TreatmentImageInput";
import TextInput from "./commons/components/form/input/TextInput";
import RichTextEditorInput from "./commons/components/form/input/RichTextEditorInput";
import Treatment from "./commons/model/treatment/Treatment";
import InputState from "./commons/model/form/InputState";
import { required } from "./commons/model/form/Validators";
import sfetch from "./commons/utils/super-fetch";
import { createRoot } from "react-dom/client";

interface TreatmentEditorProps {
  treatment?: Treatment;
  createApiUrl?: string;
  cssUrl: string;
}

interface TreatmentEditorState {
  name?: string;
  image?: string;
  formErrorMessage?: string;
}

export default class TreatmentEditor extends React.Component<
  TreatmentEditorProps,
  TreatmentEditorState
> {
  private descriptionMaxSize = 10000;
  private rteHeight = "35rem";

  constructor(props: TreatmentEditorProps) {
    super(props);
    this.save = this.save.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.state = {};
  }

  render() {
    const treatment = this.props.treatment;
    return (
      <ModalPopup
        closeModal={() => this.closeModal(false)}
        fullscreen={true}
        class="editor"
        id="treatment-editor"
      >
        {this.props.createApiUrl ? (
          <div className="title">{this.state.name || i18n["Page.Catalog.Treatments.Create"]}</div>
        ) : (
          <div className="title">
            {i18n["Global.Edit"]} {this.state.name}
          </div>
        )}
        <Form
          submitLabel={i18n["Global.Save"]}
          submit={this.save}
          formValidator={this.validateForm}
          generalErrorMsg={this.state.formErrorMessage}
        >
          <div className="h-flex with-margins">
            <div className="image-col">
              <TreatmentImageInput name="image" initialValue={treatment && treatment.imageUrl} />
            </div>
            <div className="text-col">
              <TextInput
                name="name"
                label={i18n["TreatmentViewer.Name.Title"]}
                initialValue={treatment && treatment.name}
                validators={[required()]}
                onChange={(inputState) => this.setState({ name: inputState.value })}
              />
              <RichTextEditorInput
                name="description"
                label={i18n["TreatmentViewer.Content.Title"]}
                detail={i18n["Global.OptionalField"]}
                initialValue={treatment && treatment.description}
                className="treatment-option"
                cssUrl={this.props.cssUrl}
                height={this.rteHeight}
                maxSize={this.descriptionMaxSize}
              />
            </div>
          </div>
        </Form>
      </ModalPopup>
    );
  }

  componentDidMount() {
    window.onbeforeunload = this.onClose;
  }

  componentWillUnmount(): void {
    window.onbeforeunload = null;
  }

  private onClose() {
    return "prevent-data-lose";
  }

  private closeModal(saved: boolean) {
    let path = document.location.pathname.split("/");
    path.pop();
    const hash = "#" + encodeURI(saved ? this.state.name : (this.props.treatment?.name ?? ""));
    document.location.href = path.join("/") + hash;
  }

  private async save(form: { [key: string]: InputState }) {
    const imageConfiguration =
      (this.props.treatment && this.props.treatment.imageUrl) !== form.image.value ? {} : undefined;
    if (imageConfiguration) {
      if (form.image.value) {
        const radix = "base64,";
        const radixIndex = form.image.value.indexOf(radix);
        const imageB64 = form.image.value.substring(radixIndex + radix.length);
        imageConfiguration["type"] = "UPLOAD";
        imageConfiguration["value"] = imageB64;
      } else {
        imageConfiguration["type"] = "REMOVE";
      }
    }

    const payload = {
      name: form.name.value,
      description: form.description.value,
      image: imageConfiguration,
    };

    try {
      await sfetch(this.props.createApiUrl || this.props.treatment.apiUrl, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (response) {
      this.invalidMandatoryFields(form);
      throw response;
    }
    this.componentWillUnmount();
    this.closeModal(true);
  }

  private validateForm(form: { [key: string]: InputState }): boolean {
    this.setState({ formErrorMessage: null });
    if (!form.description.value && !form.image.value) {
      this.invalidMandatoryFields(form);
      return true;
    }
    return false;
  }

  private invalidMandatoryFields(form: { [key: string]: InputState }) {
    form.description.status.setError("", true);
    form.image.status.setError("", true);
    this.setState({ formErrorMessage: i18n["TreatmentEditor.Form.Invalid"] });
  }

  static loadComponent(elementId: string, props: TreatmentEditorProps) {
    const container = document.getElementById(elementId);
    const root = createRoot(container!);
    root.render(<TreatmentEditor {...props} />);
  }
}
